import { StyledAlert, StyledDiv } from "App/global-style";
import React, { useEffect, useState } from "react";
import { AdviceProductDetailsFormValues, ChildDateOfBirth } from "./interfaces";
import { ChildDateOfBirthWrapper, RemoveChildButton } from "./styled";

import { default as StoreblocksInput } from "@storeblocks/input";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { sharedColors } from "Components/Themes/themesSharedValues";
import { TextLanguage } from "Store/texts/interfaces";
import { inputTrackId } from "Utils/analytics";
import { formatDDMMYYYY } from "Utils/dateUtils";
import { createKey } from "Utils/generalUtils";
import { isMatch } from "date-fns";
import { FormikProps } from "formik";
import { Description } from "../../adviceStyled";
import { isChildBorn } from "./childDatesOfBirthUtils";

interface Props {
  formProps: FormikProps<AdviceProductDetailsFormValues>;
  formText: any;
  lang: string;
}
export const ChildDatesOfBirthFormElements = ({
  formProps,
  formText,
  lang,
}: Props): React.ReactElement => {
  const [children, setChildren] = useState(
    formProps.values.childDatesOfBirth ?? [],
  );

  const addChild = (): void => {
    const id = createKey();
    setChildren([
      ...children,
      { id, dateOfBirth: "", inputFieldTouched: false, inputFieldError: false },
    ]);
  };

  const removeChild = (id: string): void => {
    const newChildren = children.filter((child) => child.id !== id);
    setChildren(newChildren);
    formProps.setFieldValue("childDatesOfBirth", newChildren);
  };

  const updateChildDateOfBirth = (
    child: ChildDateOfBirth,
    dateOfBirth: string,
    inputFieldTouched: boolean,
    inputFieldError: boolean,
  ): void => {
    const newChildren = [...children];
    const index = newChildren.findIndex((newChild) => newChild.id === child.id);
    newChildren[index] = {
      id: child.id,
      dateOfBirth: formatDDMMYYYY(child.dateOfBirth, dateOfBirth),
      inputFieldTouched,
      inputFieldError,
    };
    setChildren(newChildren);
    formProps.setFieldValue("childDatesOfBirth", newChildren);
  };

  const getErrorText = (child: ChildDateOfBirth): string | undefined => {
    if (
      child.dateOfBirth.length < 10 ||
      !isMatch(child.dateOfBirth, "dd.MM.yyyy")
    ) {
      return childDatesOfBirthText.validation.error[lang];
    } else if (!isChildBorn(child)) {
      return childDatesOfBirthText.validation.maxAge[lang];
    }
    return undefined;
  };

  const childDatesOfBirthText = formText.aboutYou.childDatesOfBirth;

  useEffect(() => {
    if (formProps.values.childDatesOfBirth) {
      setChildren(formProps.values.childDatesOfBirth);
    }
  }, [formProps.values.childDatesOfBirth]);

  return (
    <StyledDiv>
      <Description fw={700} size="16px">
        {"Legg inn fødelsdato på barn du forsørger"}
      </Description>
      <Description fw={400} size="14px">
        {
          "Legg inn dine egne barn - også barn over 18 år hvis de er avhengig av din økonomi for å klare seg."
        }
      </Description>
      <ChildDateOfBirthWrapper>
        {children.map((child) => (
          <div key={child.id}>
            <label htmlFor={`childDateOfBirth_${child.id}`}>
              {childDatesOfBirthText.subLabel[lang]}
            </label>
            <div>
              <StoreblocksInput
                id={`childDateOfBirth_${child.id}`}
                name={`childDateOfBirth_${child.id}`}
                aria-label="child-birth-date"
                value={child.dateOfBirth}
                onChange={(e) => {
                  updateChildDateOfBirth(
                    child,
                    e.target.value,
                    false,
                    getErrorText(child) !== undefined,
                  );
                }}
                inputProps={{
                  maxLength: 10,
                  autoFocus: true,
                  onBlur: (e) => {
                    if (e) {
                      updateChildDateOfBirth(
                        child,
                        e.target.value,
                        true,
                        getErrorText(child) !== undefined,
                      );
                    }
                  },
                }}
                trackid={inputTrackId(`childDateOfBirth_${child.id}`)}
              />
              <RemoveChildButton
                id="child-dates-of-birth__remove-child-button"
                type="button"
                iconLeft="close"
                aria-label="remove-child"
                color={sharedColors.black}
                variant="icon"
                onClick={() => removeChild(child.id)}
              />
            </div>
            {child.inputFieldTouched && getErrorText(child) && (
              <StyledAlert title={getErrorText(child) ?? ""} type="error" />
            )}
          </div>
        ))}
      </ChildDateOfBirthWrapper>
      <ThemedButton
        id="child-dates-of-birth__add-child-button"
        type="button"
        iconLeft="add"
        variant="text"
        onClick={addChild}
        trackId={childDatesOfBirthText.addChild[TextLanguage.ENGLISH]}
      >
        {childDatesOfBirthText.addChild[lang]}
      </ThemedButton>
    </StyledDiv>
  );
};
